import type { ColumnOption } from "./formkit-context";
import type { BoxProps } from "@hightouch/htui";
import type { ExtendedTypes } from "@hightouch/formkit";

export type TemplateInfo = {
  name: string;
  description: string;
  placeholders?: string[];
  inputValue?: string;
};

export type FormProps = {
  value: any;
  templates: TemplateInfo[];
  onChange: (value: any) => void;
  onReloadEligibleInlineMapperColumns: (currentSelectedColumn?: string) => void;
  useHightouchUi?: boolean;
} & OverwriteStandardInputWithArrayProps;

export type OverwriteStandardInputWithArrayProps =
  | {
      overwriteColumnsWithArrayProps: true;
      columnOptions: ColumnOption[] | undefined;
    }
  | {
      overwriteColumnsWithArrayProps?: false;
      columnOptions?: ColumnOption[] | undefined;
    };

export type JsonColumnProps = {
  selectedColumnProps: Set<string> | string[] | undefined;
  allColumnsProps: { [column: string]: Set<string> | string[] } | undefined;
};

export interface CustomColumnOption {
  label: ColumnOption["label"];
  type?: ColumnOption["type"];
  value: ColumnOption["value"];
  options?: ColumnOption["options"];
}

export type Props = {
  useHightouchUi?: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  placeholder?: string;
} & FormProps;

export type Mapping = (
  | StandardMapping
  | StaticMapping
  | VariableMapping
  | TemplateMapping
  | ObjectMapping
  | ArrayMapping
  | SpreadMapping
  | ReferenceMapping
) & {
  /**
   * Destination-field identity. Some destinations expose fields whose `to`
   * value collides across parent objects (e.g. same-name event attributes
   * under different event types), so a mapping is identified by (object, to),
   * not `to` alone. Persisted with the mapping so the picker can re-resolve
   * the exact option that was selected (see mapper-v2/utils.ts).
   */
  object?: string;
};

export interface ArrayMapping {
  type: MappingType.ARRAY;
  from?: string;
  to?: string;
  children: Mapping[];
}

export interface ObjectMapping {
  type: MappingType.OBJECT;
  from?: Mapping[];
  to?: string;
}

export interface StandardMapping {
  type: MappingType.STANDARD;
  from?: string;
  to?: string;
  ignoreNull?: boolean;
  onlyWriteIfNull?: boolean;
  disablePIIHashing?: boolean;
  fieldType?: string;
  // Field options used for further configuration (e.g. max text length for SFMC)
  typeOptions?: Record<string, string | number | boolean>;
}

export interface SpreadMapping {
  type: MappingType.SPREAD;
  /** Column holding a JSON object whose keys each become a destination field. */
  from?: string;
  /** Optional prefix; "" (or unset) spreads at the top level. */
  to?: string;
  /**
   * Optional key transform applied at sync time; unset spreads keys verbatim.
   * Only one transform per column — transforms are not chained.
   */
  keyTransform?: SpreadTransform;
  /**
   * Optional value transform applied at sync time; unset spreads values
   * verbatim. Only one transform per column — transforms are not chained.
   */
  valueTransform?: SpreadTransform;
}

/**
 * Mirrors the backend's `SpreadTransform` discriminated union
 * (@hightouch/sdk/interfaces/destination/mapping)
 * — kept app-local on purpose so the form layer never imports SDK internals
 * (that cross-boundary import pulls the SDK's whole type graph into the app
 * typecheck). Must stay in sync with the backend union. A discriminated union so
 * a transform can carry its own parameters (e.g. a Liquid `template`, an extract
 * `path`).
 */
export type SpreadTransform =
  | { type: "removeSpecialCharacters" }
  | { type: "liquid"; template: string }
  | { type: "extract"; path: string };

export type SpreadTransformType = SpreadTransform["type"];

export type SpreadTransformSide = "key" | "value";

/**
 * The transform picker options, each declaring which sides it applies to. This
 * mirrors the backend's `SPREAD_TRANSFORM_SIDES`; `spreadTransformOptions(side)`
 * filters it so the key and value pickers each show only what's valid there.
 */
export const SPREAD_TRANSFORMS: {
  label: string;
  value: SpreadTransformType;
  description: string;
  sides: SpreadTransformSide[];
}[] = [
  {
    label: "Remove special characters",
    value: "removeSpecialCharacters",
    sides: ["key", "value"],
    description:
      "Strip everything except letters and numbers (e.g. SF_ME_PRC → SFMEPRC).",
  },
  {
    label: "Custom Liquid template",
    value: "liquid",
    sides: ["key", "value"],
    description:
      "Rewrite with a Liquid template. Use {{ key }} and {{ value }} for the raw key and value. Always outputs a string.",
  },
  {
    label: "Extract from object",
    value: "extract",
    sides: ["value"],
    description:
      "Pull a field out of an object value by path (e.g. { dtype, value } with path 'value'), keeping its original type.",
  },
];

export const spreadTransformOptions = (
  side: SpreadTransformSide,
): { label: string; value: SpreadTransformType; description: string }[] =>
  SPREAD_TRANSFORMS.filter((t) => t.sides.includes(side));

/**
 * Spread entries live in a sibling sync-configuration bucket named by suffixing
 * the parent mapping field (e.g. `mappings` → `mappingsSpreadColumns`). Mirrors
 * the backend's `spreadFieldName`
 * (@hightouch/sdk/interfaces/destination/mapping) — kept app-local on purpose
 * so the form layer never imports SDK internals (that cross-boundary import
 * pulls the SDK's whole type graph into the app typecheck). Must stay in sync
 * with the backend suffix.
 */
export const SPREAD_FIELD_SUFFIX = "SpreadColumns";

export function spreadFieldName(mappingField: string): string {
  return `${mappingField}${SPREAD_FIELD_SUFFIX}`;
}

export interface StaticMapping {
  type: MappingType.STATIC;
  to: string;
  value: unknown;
  valueType: ExtendedTypes;
  fieldType?: string;
  typeOptions?: Record<string, string | number | boolean>;
}

export interface VariableMapping {
  type: MappingType.VARIABLE;
  to: string;
  variable:
    | "current-timestamp"
    | "model-name"
    | "model-id"
    | "operation"
    | "sync-id"
    | "sync-run-id";
  fieldType?: string;
  typeOptions?: Record<string, string | number | boolean>;
}

export interface TemplateMapping {
  type: MappingType.TEMPLATE;
  to: string;
  template: string;
  fieldType?: string;
  typeOptions?: Record<string, string | number | boolean>;
  ignoreNull?: boolean;
}

type StandardColumn = Omit<StandardMapping, "to">;

type StaticColumn = Omit<StaticMapping, "to">;

type VariableColumn = Omit<VariableMapping, "to">;

type TemplateColumn = Omit<TemplateMapping, "to">;

export type AdvancedColumn =
  | StandardColumn
  | StaticColumn
  | VariableColumn
  | TemplateColumn;

export type LookupCondition = {
  by: string;
  byType: ExtendedTypes;
  value: unknown;
};

export interface ReferenceMapping {
  type: MappingType.REFERENCE;
  to: string;
  lookup: {
    by: string;
    byType: ExtendedTypes;
    from: string | AdvancedColumn; //supporting advanced columns with nested properties
    object: string;
    nullIfNotFound?: boolean;
    // Constant equality conditions ANDed with the lookup match (e.g. IsActive = true)
    conditions?: LookupCondition[];
  };
  ignoreNull?: boolean;
}

// Which variables the mapper offers. A realtime journey trigger carries journey fields instead of sync context.
export type VariableScope = "sync" | "realtime-audience" | "realtime-journey";

export enum MappingType {
  STANDARD = "standard",
  STATIC = "static",
  VARIABLE = "variable",
  TEMPLATE = "template",
  OBJECT = "object",
  ARRAY = "array",
  SPREAD = "spread",
  BOOSTED = "boosted", // THIS IS A FAKE TYPE. only used on the frontend.
  REFERENCE = "reference",
}

const MappingTypeWithAssociation = {
  ...MappingType,
  REFERENCE: "reference",
};

export enum StaticType {
  STRING = "STRING",
  NUMBER = "NUMBER",
  BOOLEAN = "BOOLEAN",
  DATETIME = "DATETIME",
  NULL = "Null",
}

export const STATIC_OPTIONS = [
  {
    label: "String",
    type: StaticType.STRING,
  },
  {
    label: "Number",
    type: StaticType.NUMBER,
  },
  {
    defaultValue: true,
    label: "Boolean",
    type: StaticType.BOOLEAN,
  },
  // TODO: Implement new design for timestamp input
  // {
  //   label: "Timestamp",
  //   type: StaticType.TIMESTAMP,
  // },
  {
    label: "Null",
    type: StaticType.NULL,
  },
];

export type Option = {
  label: string;
  value: string;
  type?: string;
  disabled?: boolean;
  required?: boolean;
};

export type SelectedOptionContext = Option & {
  object?: Record<string, unknown>;
  properties?: Option[];
};

export function isMapping(m: unknown): m is Mapping {
  return (
    typeof m === "object" &&
    m !== null &&
    m !== undefined &&
    Object.keys(MappingTypeWithAssociation).includes(m["type"]?.toUpperCase())
  );
}

export function isArrayMapping(m: unknown): m is ArrayMapping {
  return (
    isMapping(m) && m.type === MappingType.ARRAY && m.children !== undefined
  );
}

export type InlineMapperProps = {
  isDisabled?: boolean;
  type: "array" | "object";
  currentDepth: number;
  required?: boolean;
  errorPrefix: string;
  parentMapping?: Mapping;
  /**
   * Optional override for source column options shown in nested inline mappers.
   * Used for event forwarding array elementOptions (scope nested mappings to the selected array element).
   */
  columnOptionsOverride?: ColumnOption[] | undefined;
  jsonColumnProperties: JsonColumnProps;
  enabledNestedInlineMapper: boolean;
  onChangeJsonColumnProperties: React.Dispatch<
    React.SetStateAction<JsonColumnProps>
  >;
  onReloadEligibleInlineMapperColumns: (currentSelectedColumn?: string) => void;
  retrieveErrorMessage: (
    errors: Record<string, unknown>,
    mappingCount: number,
    name: string,
  ) => string;
  onChange: (value: Mapping[]) => void;
  value: ArrayMapping | ObjectMapping;
  outerContainerSx?: BoxProps["sx"];
  selectedOptionContext: SelectedOptionContext[] | undefined;
  overwriteColumnsWithArrayProps?: boolean;
  useHightouchUi?: boolean;
  supportOnlyWriteIfNull?: boolean;
  templates?: { name: string; description: string; placeholders?: string[] }[];
};
